import React, { useState, useEffect } from 'react'
import s from './maps.module.css'

type Props = {}

interface ISuggestItem {
  label: string
  coordinates: [number, number]
}

const Searcher = (props: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState<ISuggestItem[]>([])
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const w = window as any
    const ymaps = w.ymaps
    
    const query = searchQuery.trim()
    if (!query || !ymaps || !ymaps.geocode) {
      setSuggestions([])
      return
    }

    // Дебаунс для предотвращения лишних запросов к API при быстром вводе
    const timer = setTimeout(() => {
      ymaps.geocode(query, {
        // Жестко ограничиваем подсказки только границами Беларуси
        boundedBy: [[51.2, 23.1], [56.2, 32.8]],
        strictBounds: true,
        results: 5 // Выводим топ-5 совпадений
      })
        .then((res: any) => {
          const items: ISuggestItem[] = []
          res.geoObjects.each((geoObject: any) => {
            items.push({
              label: geoObject.getAddressLine(),
              coordinates: geoObject.geometry.getCoordinates()
            })
          })
          setSuggestions(items)
        })
        .catch(() => {
          setSuggestions([])
        })
    }, 350)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Логика выбора адреса из списка подсказок или отправки формы
  const handleSelectAddress = (coords: [number, number], label: string) => {
    const w = window as any
    const ymaps = w.ymaps
    const mapInstance = w.yandexMapInstance

    setSearchQuery(label)
    setIsOpen(false)

    if (ymaps && mapInstance && mapInstance.setCenter) {
      // Удаляем предыдущий маркер поиска, если он существовал
      if (w.currentSearchPlacemark) {
        mapInstance.geoObjects.remove(w.currentSearchPlacemark)
      }

      // Создаем новую чистую точку-маркер на карте
      const searchPlacemark = new ymaps.Placemark(coords, {
        balloonContent: `<b>Искомый адрес:</b><br/>${label}`
      }, {
        preset: 'islands#blueHomeIcon',
        iconColor: '#005bff'
      })

      w.currentSearchPlacemark = searchPlacemark
      mapInstance.geoObjects.add(searchPlacemark)

      // Плавный полет камеры к выбранному дому
      mapInstance.setCenter(coords, 17, {
        duration: 800,
        timingFunction: 'ease-in-out'
      })

      // Открытие балуна над точкой по завершении полета
      setTimeout(() => {
        if (searchPlacemark.balloon && searchPlacemark.balloon.open) {
          searchPlacemark.balloon.open()
        }
      }, 850)
    }
  }

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Если пользователь нажал Enter и у нас есть хотя бы одна подсказка - выбираем первую
    if (suggestions.length > 0) {
      handleSelectAddress(suggestions[0].coordinates, suggestions[0].label)
    }
  }

  // Очистка маркера при полном стирании строки поиска
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchQuery(val)
    setIsOpen(true)

    if (!val.trim()) {
      const w = window as any
      if (w.yandexMapInstance && w.currentSearchPlacemark) {
        w.yandexMapInstance.geoObjects.remove(w.currentSearchPlacemark)
        w.currentSearchPlacemark = null
      }
    }
  }

  return (
    <div className={s.searchContainer}>
      <form onSubmit={handleSearchSubmit} className={s.searchForm}>
        <input
          type="text"
          className={s.search}
          placeholder="Поиск дома в РБ..."
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)} // Задержка, чтобы успел отработать клик по элементу
          autoComplete="off"
        />
      </form>

      {/* Отрисовка кастомного выпадающего списка на чистом React */}
      {isOpen && suggestions.length > 0 && (
        <ul className={s.suggestList}>
          {suggestions.map((item, index) => (
            <li
              key={index}
              className={s.suggestItem}
              onClick={() => handleSelectAddress(item.coordinates, item.label)}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default Searcher
